import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Search } from "lucide-react";

const CodingTests = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "You have been signed out successfully.",
      });
      navigate("/");
    }
  };

  const skillCategories = [
    "All", "Skill-based", "Backend", "Containers", "Data engineering",
    "Database", "DevOps", "Frontend", "Hybrid mobile", "Infrastructure",
    "Java", "Microsoft", "Mobile", "NodeJS", "Other", "PHP", "Python",
    "React", "Soft skills", "Turing test", "Vue"
  ];

  const quizzes = [
    { name: "Apollo GraphQL", icon: "A", duration: 15, status: "not_started" },
    { name: "AWS", icon: "AWS", duration: 30, status: "not_started" },
    { name: "C#", icon: "C#", duration: 15, status: "not_started" },
    { name: "D3.js", icon: "D3", duration: 15, status: "not_started" },
    { name: "GraphQL", icon: "GQL", duration: 15, status: "not_started" },
    { name: "Javascript", icon: "JS", duration: 15, status: "passed" },
    { name: "React Native", icon: "RN", duration: 15, status: "not_started" },
    { name: "ReactJS", icon: "⚛️", duration: 15, status: "passed" },
    { name: "Scala", icon: "S", duration: 32, status: "not_started" },
    { name: "TypeScript", icon: "TS", duration: 15, status: "passed" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Coding tests</h1>
          <div className="space-x-4">
            <Button variant="outline" onClick={() => navigate("/developer-dashboard")}>
              Edit profile
            </Button>
            <Button onClick={() => navigate("/developer/resume")}>Upload resume</Button>
            <Button 
              variant="destructive" 
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <p className="text-gray-700 mb-6">
              Are you interested in using these skills? This is our way of knowing which jobs to match you with. Please take quizzes for the skills you want to continue using in your next career.
            </p>

            <div className="flex flex-wrap gap-2 mb-6">
              {skillCategories.map((category) => (
                <Button
                  key={category}
                  variant={category === "All" ? "secondary" : "outline"}
                  className="rounded-full"
                >
                  {category}
                </Button>
              ))}
            </div>

            <div className="relative mb-8">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search skills"
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {quizzes.map((quiz) => (
              <div key={quiz.name} className="bg-white rounded-lg p-6 shadow-sm border">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-2xl font-bold">
                    {quiz.icon}
                  </div>
                  <h3 className="font-semibold">{quiz.name}</h3>
                  <div className="text-sm text-gray-500 flex items-center gap-1">
                    <span>⏱️</span>
                    <span>{quiz.duration} min</span>
                  </div>
                  {quiz.status === "passed" ? (
                    <div className="text-green-600 font-medium">
                      Completed
                    </div>
                  ) : (
                    <Button variant="outline" className="w-full">
                      Start Quiz
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodingTests;